import Renderer from 'apollo-e/rendering/Renderer';
import Object3D from 'apollo-e/rendering/Object3D';
import { Shader, VertexShader, FragmentShader } from 'apollo-e/rendering/Shader';
import { TextureSet, EMPTY_TEXTURE_SET } from 'apollo-e/rendering/Texture';
import SDFText from 'apollo-e/text/SDFText';

import Vector2 from 'apollo-e/math/Vector2';
import Vector3 from 'apollo-e/math/Vector3';
// import Transform3D from 'apollo-e/transformNodes/Transform3D';

import opacities from 'apollo-e/rendering/OPACITIES';
import RGBA from 'apollo-e/colors/RGBA';

const defaultVertexScript = require('./shaders/vertex/sdfText3D.vert');
const defaultFragmentScript = require('./shaders/fragment/sdfText3D.frag');

const defaultVertexShader = new VertexShader(defaultVertexScript, ['aPosition', 'aSize', 'aRotation', 'aColor', 'aOffset', 'aUV'], ['uMMatrix', 'uCameraPosition']);
const defaultFragmentShader = new FragmentShader(defaultFragmentScript, ['uAmbientLight', 'uDirectionalLight', 'uLightDirection'], ['uTexture']);

const bytesPerCharacter = 32 * 4;
// const bytesPerCharacter = 20 * 4;

// function renderShader (this: BatchShader, gl: WebGLRenderingContext, vertexBuffer: Uint8Array, byteCount: number, vpMatrix: Array<number>) {
// 	const program = this.shader.program;
// 	const attributes = program.attributes;

// 	// const bytesPerPosition = 4 * 3;
// 	// const bytesPerRotation = 4 * 1;
// 	// const bytesPerOffset = 4 * 2;
// 	// const bytesPerColor = 1 * 4;
// 	// const bytesPerUV = 2 * 2;
// 	// const totalBytes = bytesPerPosition + bytesPerRotation + bytesPerOffset + bytesPerColor + bytesPerUV;

// 	const bytesPerPosition = 4 * 3;
// 	const bytesPerOffset = 4 * 2;
// 	const totalBytes = bytesPerPosition + bytesPerOffset;

// 	let byteOffset = 0;
// 	// gl.vertexAttribPointer(attributes.aPosition,    3, gl.FLOAT,          false, totalBytes, 0);
// 	// gl.vertexAttribPointer(attributes.aRotation,          1, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerPosition);
// 	// gl.vertexAttribPointer(attributes.aOffset,      2, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerRotation);
// 	// gl.vertexAttribPointer(attributes.aColor,             4, gl.UNSIGNED_BYTE,  true,  totalBytes, byteOffset += bytesPerOffset);
// 	// gl.vertexAttribPointer(attributes.aUV, 2, gl.UNSIGNED_SHORT, true,  totalBytes, byteOffset += bytesPerColor);
// 	gl.vertexAttribPointer(attributes.aPosition,    3, gl.FLOAT,          false, totalBytes, 0);
// 	gl.vertexAttribPointer(attributes.aOffset,      2, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerPosition);

// 	// const uniforms = program.uniforms;
// 	// gl.uniformMatrix4fv(uniforms.uVPMatrix, false, vpMatrix);

// 	// Uploading vertex buffer
// 	// bufferData seems to behave better than bufferSubData on Safari
// 	// gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexBuffer.subarray(0, byteCount));
// 	// gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexBuffer);
// 	gl.bufferData(gl.ARRAY_BUFFER, vertexBuffer, gl.DYNAMIC_DRAW);

// 	// Rendering
// 	// console.error('Rendering', byteCount / 48, 'Sprites.', 6 * byteCount / 48, 'Vertices.', byteCount, 'Bytes.')
// 	gl.drawElements(gl.TRIANGLES, 6 * byteCount / bytesPerCharacter, gl.UNSIGNED_SHORT, 0);

// 	// console.error('rendering!', bytesPerCharacter)
// }

// const defaultShader = new BatchShader(defaultVertexShader, defaultFragmentShader, renderShader);
const defaultShader = new Shader(defaultVertexShader, defaultFragmentShader);

const shaders = {
	default: defaultShader
};

class Character3D {
	position: Vector3;
	rotation: number;
	color: RGBA;
	scales: Vector2;
	pivots: Vector2;
	textureSet: TextureSet;
	payload: any;

	constructor () {
		this.position = new Vector3(0, 0, 0);
		this.rotation = 0;
		this.scales = new Vector2(1, 1);
		this.pivots = new Vector2(0, 0);
		this.color = new RGBA(0, 0, 0, 1);
		this.textureSet = EMPTY_TEXTURE_SET;
		this.payload = null;
	}

	get scale () {
		return (this.scales.x + this.scales.y) / 2;
	}

	set scale (scale) {
		this.scales.x = scale;
		this.scales.y = scale;
	}

	get pivot () {
		return (this.pivots.x + this.pivots.y) / 2;
	}

	set pivot (pivot) {
		this.pivots.x = pivot;
		this.pivots.y = pivot;
	}
}

export default class BitmapText3D extends Object3D {
	shaders = shaders;

	bitmapText: SDFText;
	characters: Array<Character3D>;

	constructor () {
		super();
		this.opacity = opacities.TRANSLUSCENT;
		this.castShadow = false;
		this.characters = [];
		this.bitmapText = new SDFText(() => {
			this.updateCharacters();
		}, () => {
			this.updateTextureSet();
		});
	}

	setAllCharactersColor (r: number, g: number, b: number, a: number = 1) {
		for (let c = 0; c < this.characters.length; c++) {
			const color = this.characters[c].color;
			color.r = r;
			color.g = g;
			color.b = b;
			color.a = a;
		}
	}

	setAllCharactersAlpha (alpha: number) {
		for (let c = 0; c < this.characters.length; c++) {
			this.characters[c].color.a = alpha;
		}
	}

	private updateCharacters () {
		const text = this.bitmapText.string;
		this.characters.length = 0;
		for (let c = 0; c < text.length; c++) {
			this.characters[c] = new Character3D();
		}
	}

	private updateTextureSet () {
		this.textureSet = this.bitmapText.textureSet;
	}

	render (renderer: Renderer, shader: Shader) {
		this.bitmapText.computeLayout();

		const textCharacters = this.bitmapText.characters;
		if (textCharacters.length === 0) {
			return;
		}

		const floatView = renderer.floatView;
		const uLongView = renderer.uLongView;

		let offset = 0;
		const textSize = this.bitmapText.size;
		const characters = this.characters;
		for (let c = 0; c < characters.length; c++) {
			const character = characters[c];
			const textCharacter = textCharacters[c];

			const position = character.position;
			floatView[offset + 0] = floatView[offset + 9]  = floatView[offset + 18] = floatView[offset + 27] = textCharacter.x + position.x;
			floatView[offset + 1] = floatView[offset + 10] = floatView[offset + 19] = floatView[offset + 28] = textCharacter.y + position.y;
			floatView[offset + 2] = floatView[offset + 11] = floatView[offset + 20] = floatView[offset + 29] = position.z;

			floatView[offset + 3] = floatView[offset + 12] = floatView[offset + 21] = floatView[offset + 30] = character.rotation;

			const scales = character.scales;
			const scaleX = scales.x;
			const scaleY = scales.y;
	
			const pivots = character.pivots;
			const x0 = -pivots.x * scaleX;
			const y0 = -pivots.y * scaleY;
	
			const x1 = x0 + textCharacter.width * scaleX;
			const y1 = y0 + textCharacter.height * scaleY;
	
			floatView[offset + 4] = x0;
			floatView[offset + 5] = y0;
			floatView[offset + 13] = x1;
			floatView[offset + 14] = y0;
			floatView[offset + 22] = x0;
			floatView[offset + 23] = y1;
			floatView[offset + 31] = x1;
			floatView[offset + 32] = y1;

			floatView[offset + 6] = floatView[offset + 15] = floatView[offset + 24] = floatView[offset + 33] = textSize;

			const color = character.color;
			const colorCompressed = ((255 * color.r) & 0xff) + (((255 * color.g) & 0xff) << 8) + (((255 * color.b) & 0xff) << 16) + (((255 * color.a) & 0xff) << 24);
			uLongView[offset + 7] = uLongView[offset + 16] = uLongView[offset + 25] = uLongView[offset + 34] = colorCompressed;

			const uvs = textCharacter.fontCharacter.uvs;

			uLongView[offset + 8] = uvs.bottomLeft;
			uLongView[offset + 17] = uvs.bottomRight;
			uLongView[offset + 26] = uvs.topLeft;
			uLongView[offset + 35] = uvs.topRight;

			offset += 36;
		}

		const gl = renderer.gl;
		const program = shader.program;
		const attributes = program.attributes;
	
		const bytesPerPosition = 4 * 3;
		const bytesPerRotation = 4 * 1;
		const bytesPerOffset = 4 * 2;
		const bytesPerSize = 4 * 1;
		const bytesPerColor = 1 * 4;
		const bytesPerUV = 2 * 2;
		const totalBytes = bytesPerPosition + bytesPerRotation + bytesPerOffset + bytesPerSize + bytesPerColor + bytesPerUV;
	
		gl.bindBuffer(gl.ARRAY_BUFFER, renderer.vertexBufferBinder);

		let byteOffset = 0;
		gl.vertexAttribPointer(attributes.aPosition,          3, gl.FLOAT,          false, totalBytes, 0);
		gl.vertexAttribPointer(attributes.aRotation,          1, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerPosition);
		gl.vertexAttribPointer(attributes.aOffset,      2, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerRotation);
		gl.vertexAttribPointer(attributes.aSize,              1, gl.FLOAT,          false, totalBytes, byteOffset += bytesPerOffset);
		gl.vertexAttribPointer(attributes.aColor,             4, gl.UNSIGNED_BYTE,  true,  totalBytes, byteOffset += bytesPerSize);
		gl.vertexAttribPointer(attributes.aUV, 2, gl.UNSIGNED_SHORT, true,  totalBytes, byteOffset += bytesPerColor);

		// Uploading vertex buffer
		// bufferData seems to behave better than bufferSubData on Safari
		// gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexBuffer.subarray(0, byteCount));
		// gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, renderer.byteView, gl.DYNAMIC_DRAW);
	
		// Rendering
		// console.error('Rendering', byteCount / 48, 'Sprites.', 6 * byteCount / 48, 'Vertices.', byteCount, 'Bytes.')
		gl.drawElements(gl.TRIANGLES, 6 * characters.length, gl.UNSIGNED_SHORT, 0);
	
	}

	renderDefault (renderer: Renderer) {
		this.render(renderer, defaultShader);
	}
}
